import React, { useEffect, useState } from "react"
import { Modal, Segmented, message } from "antd"
import { gen, swimminglyApi } from "./utils"
import InlineTextEdit from "./InlineTextEdit"
import CenterSpin from "./CenterSpin"
import { IUser } from "./utils/sharedDataTypes"

interface IExportEntriesModal {
  isVisible: boolean
  clubId: number | string
  meetId: number
  exportClubIds?: Array<number | string>
  user?: IUser | null
  updateVisible: (val: any) => void
}

function ExportEntriesModal({
  isVisible,
  clubId,
  meetId,
  exportClubIds,
  user,
  updateVisible,
}: IExportEntriesModal) {
  type ClubCodes = {
    clubId: number
    name?: string
    lsc: string
    teamCode: string
  }

  const [clubCodesList, setClubCodesList] = useState<ClubCodes[]>([])
  const [loading, setLoading] = useState(false)
  const [unknownSwimmerCount, setUnknownSwimmerCount] = useState(0)
  const [meetInfo, setMeetInfo] = useState<{ meetName: string; meetDate: string } | null>(null)

  const LSC_RE = /^[A-Z]{2}$/
  const TEAM_CODE_RE = /^[A-Za-z0-9]{1,5}$/
  const isLscValid = (lsc: string) => LSC_RE.test(lsc)
  const isTeamCodeValid = (tc: string) => TEAM_CODE_RE.test(tc)
  const invalidClubs = clubCodesList.filter(
    (c) => !isLscValid(c.lsc) || !isTeamCodeValid(c.teamCode),
  )
  const allCodesValid = invalidClubs.length === 0
  const [exportFormat, setExportFormat] = useState<"sd3" | "hy3" | "cl2" | "hy3cl2">("hy3cl2")

  useEffect(() => {
    let mounted = true

    const load = async () => {
      if (!isVisible) return
      setLoading(true)

      const numericExportIds =
        exportClubIds && exportClubIds.length
          ? exportClubIds
              .map((id) => parseInt(id.toString(), 10))
              .filter((id) => Number.isFinite(id))
          : []

      const fallbackClubId = parseInt(clubId.toString(), 10)
      const targetClubIds =
        numericExportIds.length > 0 ? numericExportIds : [fallbackClubId]

      const normalizeCode = (val?: string | null) => {
        if (!val || val.trim() === "") return "not set"
        return val
      }

      try {
        const [clubResults, entriesData, meetInfoData] = await Promise.all([
          Promise.all(
            targetClubIds.map((id) =>
              swimminglyApi
                .post(gen("/api/lscAndClubCode"))
                .body({ clubId: id })
                .then((clubResponse) => {
                  if (clubResponse.status === "success") {
                    const {
                      sd3_lsc,
                      sd3_team_code,
                      name_long,
                    } = clubResponse.data

                    return {
                      clubId: id,
                      name: name_long,
                      lsc: normalizeCode(sd3_lsc),
                      teamCode: normalizeCode(sd3_team_code),
                    } as ClubCodes
                  }

                  return {
                    clubId: id,
                    name: undefined,
                    lsc: "not set",
                    teamCode: "not set",
                  } as ClubCodes
                }),
            ),
          ),
          swimminglyApi
            .post(gen("/api/countUnknownSwimmersInEntries"))
            .body({ meetId }),
          swimminglyApi.get(gen(`/api/getMeetInformation/${meetId}`)),
        ])

        if (mounted) {
          setClubCodesList(clubResults)

          if (meetInfoData?.meet?.meetName) {
            setMeetInfo({
              meetName: meetInfoData.meet.meetName,
              meetDate: meetInfoData.meet.meet_date ?? "",
            })
          }

          if (entriesData.status === "success") {
            const theCounts: {
              club_id: number
              numEntries: number | string
            }[] = entriesData.data

            const totalUnknown = theCounts
              .filter((x) => targetClubIds.includes(x.club_id))
              .reduce((sum, row) => {
                const val = parseInt(row.numEntries.toString(), 10)
                return sum + (Number.isNaN(val) ? 0 : val)
              }, 0)

            setUnknownSwimmerCount(totalUnknown)
          }
        }
      } catch (e) {
        if (mounted) {
          message.error("Could not load club codes for export")
        }
      } finally {
        if (mounted) setLoading(false)
      }
    }

    load()

    return () => {
      mounted = false
    }
  }, [isVisible, clubId, meetId, exportClubIds])

  const handleOk = async () => {
    setLoading(true)

    const toHytekDateStamp = (iso: string): string => {
      const d = new Date(iso)
      if (isNaN(d.getTime())) return ""
      const day = String(d.getUTCDate()).padStart(2, "0")
      const month = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" }).replace(".", "")
      return `${day}${month}${d.getUTCFullYear()}`
    }

    const safeMeetName = (meetInfo?.meetName ?? `Meet_${meetId}`)
      .replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 40)
    const dateStamp = meetInfo?.meetDate ? toHytekDateStamp(meetInfo.meetDate) : ""
    const numericExportIds =
      exportClubIds && exportClubIds.length
        ? exportClubIds.map((id) => parseInt(id.toString(), 10)).filter((id) => Number.isFinite(id))
        : []
    const isAllClubs = numericExportIds.length !== 1
    const teamCode = isAllClubs
      ? "All Clubs"
      : (clubCodesList.find((c) => c.clubId === numericExportIds[0])?.teamCode || "CLUB")
    const dateSuffix = dateStamp ? `-${dateStamp}` : ""
    const entriesBase = `${teamCode}-Entries-${safeMeetName}${dateSuffix}`
    // Persist LSC and team code for each club in the list
    for (const club of clubCodesList) {
      const lscCode =
        !club.lsc || ["", "not set"].includes(club.lsc.trim())
          ? null
          : club.lsc
      const teamCode =
        !club.teamCode || ["", "not set"].includes(club.teamCode.trim())
          ? null
          : club.teamCode

      // If both are null, nothing to update for this club
      if (!lscCode && !teamCode) {
        continue
      }

      // eslint-disable-next-line no-await-in-loop
      await updateLscOrTeamCode(club.clubId, {
        lscCode,
        teamCode,
      })
    }

    try {
      const clubIdsParam =
        exportClubIds && exportClubIds.length
          ? exportClubIds
              .map((id) => id.toString())
              .filter((id) => id.trim().length > 0)
              .join(",")
          : ""

      if (exportFormat === "sd3") {
        const url =
          gen(`/api/meetEntriesSD3/${meetId}`) +
          (clubIdsParam ? `?clubIds=${encodeURIComponent(clubIdsParam)}` : "")
        await swimminglyApi.download(url, `${entriesBase}.sd3`, document)
      } else {
        const params = new URLSearchParams({ format: exportFormat })
        if (clubIdsParam) params.set("clubIds", clubIdsParam)
        const url = gen(`/api/meetEntriesExport/${meetId}`) + `?${params.toString()}`
        await swimminglyApi.download(url, `${entriesBase}.zip`, document)
      }
    } catch (error) {
      message.error("Could not download meet entries")
    }



    updateVisible(false)
  }


  const handleCancel = async () => {
    updateVisible(false)
  }

  const updateLscOrTeamCode = async (
    clubIdToUpdate: number,
    value: {
    lscCode: string | null
    teamCode: string | null
    },
  ) => {
    const theData: {
      clubId: number
      newLscCode: string
      newTeamCode: string
    } = { clubId: clubIdToUpdate, newLscCode: "", newTeamCode: "" }

    if (value.lscCode) {
      theData.newLscCode = value.lscCode
    }

    if (value.teamCode) {
      theData.newTeamCode = value.teamCode
    }

    const clubResponse = await fetch(gen("/api/updateLscAndClubCode"), {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "post",
      body: JSON.stringify(theData),
    }).then((response) => response.json())

    if (clubResponse.status === "success") {
      const {
        sd3_lsc,
        sd3_team_code,
      }: { sd3_lsc: string; sd3_team_code: string } = clubResponse.data

      const normalizeCode = (val?: string | null) => {
        if (!val || val.trim() === "") return "not set"
        return val
      }

      const normalizedLsc = normalizeCode(sd3_lsc)
      const normalizedTeamCode = normalizeCode(sd3_team_code)

      setClubCodesList((prev) =>
        prev.map((club) =>
          club.clubId === clubIdToUpdate
            ? {
                ...club,
                lsc: normalizedLsc,
                teamCode: normalizedTeamCode,
              }
            : club,
        ),
      )
    }
  }

  return (
    <Modal
      open={isVisible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={800}
      title="Export Meet Entries"
      okButtonProps={{ disabled: !allCodesValid || loading }}
      okText="Download"
    >
      <div>
        {loading ? <CenterSpin /> : null}
        <div style={{ marginBottom: 16 }}>
          <strong style={{ marginRight: 12 }}>Export format:</strong>
          <Segmented
            value={exportFormat}
            onChange={(val) => setExportFormat(val as typeof exportFormat)}
            options={[
              {
                label: (
                  <span>
                    HY3 + CL2{" "}
                    <span
                      style={{
                        background: "#1677ff",
                        color: "#fff",
                        borderRadius: 4,
                        fontSize: 10,
                        padding: "1px 5px",
                        marginLeft: 4,
                        fontWeight: 600,
                        letterSpacing: 0.3,
                        verticalAlign: "middle",
                      }}
                    >
                      Recommended
                    </span>
                  </span>
                ),
                value: "hy3cl2",
              },
              { label: "HY3", value: "hy3" },
              { label: "CL2", value: "cl2" },
              { label: "SD3", value: "sd3" },
            ]}
          />
          {exportFormat !== "sd3" && (
            <span style={{ marginLeft: 12, color: "#888", fontSize: 12 }}>
              Downloads as a .zip file
            </span>
          )}
        </div>
        <p>
          {exportFormat === "sd3"
            ? "You are about to download the SD3/SDIF file for the selected club(s)\u2019 entries for this meet."
            : `You are about to download a ZIP containing the ${
                exportFormat === "hy3cl2" ? "HY3 and CL2 files" : exportFormat.toUpperCase() + " file"
              } for the selected club(s)\u2019 entries for this meet.`}{" "}
          Please review and, if needed, update the LSC and team code for each club shown below:
        </p>
        {clubCodesList.map((club) => {
          const lscError = !isLscValid(club.lsc)
          const teamCodeError = !isTeamCodeValid(club.teamCode)
          return (
            <div key={club.clubId} style={{ marginBottom: 12 }}>
              <strong>
                {club.name && club.name.trim().length > 0
                  ? club.name
                  : `Club #${club.clubId}`}
              </strong>
              <ul>
                <li>
                  LSC code:{" "}
                  <InlineTextEdit
                    updateTheValue={(val: string) => {
                      const newValue = val.trim().slice(0, 2).toUpperCase()
                      setClubCodesList((prev) =>
                        prev.map((c) =>
                          c.clubId === club.clubId
                            ? { ...c, lsc: newValue || "not set" }
                            : c,
                        ),
                      )
                    }}
                  >
                    {club.lsc}
                  </InlineTextEdit>
                  {lscError && (
                    <span style={{ color: "#ff4d4f", fontSize: 12, marginLeft: 8 }}>
                      Must be exactly 2 uppercase letters (e.g. “SE”)
                    </span>
                  )}
                </li>
                <li>
                  Team code:{" "}
                  <InlineTextEdit
                    updateTheValue={(val: string) => {
                      const newValue = val.trim().toUpperCase()
                      setClubCodesList((prev) =>
                        prev.map((c) =>
                          c.clubId === club.clubId
                            ? { ...c, teamCode: newValue || "not set" }
                            : c,
                        ),
                      )
                    }}
                  >
                    {club.teamCode}
                  </InlineTextEdit>
                  {teamCodeError && (
                    <span style={{ color: "#ff4d4f", fontSize: 12, marginLeft: 8 }}>
                      Must be 1–5 alphanumeric characters
                    </span>
                  )}
                </li>
              </ul>
            </div>
          )
        })}
        {!allCodesValid && !loading && (
          <div
            style={{
              background: "#fff2f0",
              border: "1px solid #ffccc7",
              borderRadius: 6,
              padding: "8px 12px",
              marginBottom: 12,
              color: "#cf1322",
              fontSize: 13,
            }}
          >
            Fix the LSC and team code errors above before downloading.
          </div>
        )}
        {unknownSwimmerCount > 0 ? (
          <React.Fragment>
            <br />
            <p>
              You have unknown swimmers in your meet entries. These swimmers
              will not be exported in your sd3 file. We recommend that you go to
              your meet entries and update them to the correct swimmers before
              exporting.
            </p>
          </React.Fragment>
        ) : null}
        <br />
        <p>Additionally, please note the following for accuracy:</p>
        <p>
          The SD3/SDIF file uses a universally generated USA Swimming ID per
          swimmer based on the following info:
        </p>
        <p>
          <b> Example : (11/03/1987) Charles G Houchin: 110387CHAGHOUC</b>{" "}
          <br />
          --DoB: [MMDDYY] --First three letters of your legal first name --First
          letter of your middle name --First four letters of your last name. If
          your first or last names don't have enough letters [or you don't have
          a middle name or middle initial], an * [asterisk] will be used to fill
          in the blanks.
        </p>
        <p>
          For your convenience, the Swimmingly Clubhouse automatically does this
          for you (based on your swimmer info under 'Manage Roster') upon
          exporting this file. Some other team management systems allow users to
          manually change this ID. To be compatible, we recommend you maintain
          the automatically generated ID in the system to which you're
          attempting to import this file.
        </p>
        <p>
          Note: the USA Swimming ID described above is not related to each
          Swimmer ID used on the app or in the Clubhouse
        </p>
        <p>
          LSC and Club Codes, editable under your 'Manage Club' tab must match
          the same codes you have in your team management system in which you
          wish to import. Hint: LSC code is usually your state's abbreviation
          and Club code is usually your club's 4 letter abbreviation. If you are
          trying to map to an existing swimmer in a separate team management
          system that contains the middle initial for each swimmer, you must
          include that information for each swimmer under 'Manage Roster'.
          Otherwise, a new swimmer will be created. Double check the file
          extension when you download this file (*.SD3). If it does not end with
          this, use a different web browser (we recommend Chrome).
        </p>
      </div>
    </Modal>
  )
}

export default ExportEntriesModal
